// nav.component.ts

import { Component, OnInit, computed, inject } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { AuthService } from '../../../features/auth/services/auth.service';
import { LanguageService } from '@core/services/language.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ContactDialogComponent } from '../contact-dialog/contact-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../base-component';
import { TourService } from '@core/services/tour.service';
import { ThemeService, ThemeMode } from '@core/services/theme.service';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { BusinessModeService } from '@core/services/business-mode.service';
import { ExternalLink } from '@core/config/business-mode.config';

@Component({
    selector: 'app-nav',
    templateUrl: './nav.component.html',
    styleUrls: ['./nav.component.scss'],
    imports: [
        CommonModule,
        MatDividerModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        MatDialogModule,
        TranslateModule,
        NotificationBellComponent,
        RouterModule
    ],
    standalone: true
})
export class NavComponent extends BaseComponent implements OnInit {

private router = inject(Router);
private dialog = inject(MatDialog);
private authService = inject(AuthService);
private readonly languageService = inject(LanguageService);
private translate = inject(TranslateService);
readonly tourService = inject(TourService);
readonly themeService = inject(ThemeService);
readonly businessModeService = inject(BusinessModeService);

hasNavItem(key: string): boolean {
  const items = this.businessModeService.navItems();
  if (items.length === 0) return true;
  return items.includes(key);
}
readonly isAuthenticated = this.authService.isAuthenticated;
readonly user = this.authService.user;
  currentUser = this.authService.user;
  isMenuOpen = false;
  isMobileMenuOpen = false;
  activeSearchLink: ExternalLink | null = null;

  setActiveSearchLink(link: ExternalLink): void {
    this.activeSearchLink = link;
  }

  openNavSearch(query: string): void {
    if (!this.activeSearchLink?.searchUrl || !query.trim()) return;
    const url = this.activeSearchLink.searchUrl.replace('{q}', encodeURIComponent(query.trim()));
    window.open(url, '_blank', 'noopener');
  }

  get userInitials(): string {
    const user = this.authService.user();
    if (!user) return '?';
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    return (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || '?';
  }

  ngOnInit() {
    // Auth state is already initialized from localStorage in AuthService
    // Protected routes have AuthGuard which will verify auth when needed
    // No need to call checkAuth() here - it causes issues on public pages

    // Start tour for demo users on page refresh (only within an active demo session)
    if (this.isDemo && sessionStorage.getItem('demo_just_logged_in')) {
      setTimeout(() => this.tourService.startIfFirstVisit(), 1000);
    }
  }
 readonly getCurrentUser = computed(() => this.authService.user());

  get isAdmin(): boolean {
    const user = this.authService.user();
    return user?.role === 'Admin' || false;
  }

  get isDemo(): boolean {
    const user = this.authService.user();
    return !!user && (
      user.email?.toLowerCase().includes('demo') ||
      user.username?.toLowerCase().includes('demo') ||
      user.role?.toLowerCase() === 'demo'
    );
  }

  readonly availableLanguages = [
    { code: 'he', nativeName: 'עברית' },
    { code: 'en', nativeName: 'English' },
    { code: 'ar', nativeName: 'العربية' },
    { code: 'es', nativeName: 'Español' },
    { code: 'fr', nativeName: 'Français' },
    { code: 'de', nativeName: 'Deutsch' },
    { code: 'it', nativeName: 'Italiano' },
    { code: 'pt', nativeName: 'Português' },
    { code: 'nl', nativeName: 'Nederlands' },
    { code: 'pl', nativeName: 'Polski' },
    { code: 'ro', nativeName: 'Română' },
    { code: 'el', nativeName: 'Ελληνικά' },
    { code: 'uk', nativeName: 'Українська' },
    { code: 'ru', nativeName: 'Русский' },
    { code: 'tr', nativeName: 'Türkçe' },
    { code: 'zh', nativeName: '中文' },
    { code: 'ja', nativeName: '日本語' },
    { code: 'ko', nativeName: '한국어' },
    { code: 'hi', nativeName: 'हिन्दी' },
    { code: 'th', nativeName: 'ไทย' },
    { code: 'vi', nativeName: 'Tiếng Việt' },
    { code: 'id', nativeName: 'Bahasa Indonesia' },
    { code: 'ms', nativeName: 'Bahasa Melayu' },
    { code: 'am', nativeName: 'አማርኛ' },
  ];

  get currentLanguageDisplay(): string {
    return this.languageService.getCurrentLanguage()?.toUpperCase() || 'EN';
  }

  setTheme(mode: ThemeMode): void {
    this.themeService.setTheme(mode);
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  // Kept in step with shell-v2's openSupport(). The two shells have already
  // drifted apart once over a user-menu entry (Business Settings) — anything
  // added to one menu belongs in the other.
  openSupport(): void {
    this.dialog.open(ContactDialogComponent, {
      panelClass: 'contact-dialog-panel',
      maxWidth: '90vw',
      autoFocus: false
    });
  }

  logout() {
    this.authService.logout()
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  async login() {
    this.router.navigate(['/login']);
  }

  setLanguage(lang: string) {
    this.languageService.setLanguage(lang);
  }

  getCurrentLanguage() {
    return this.languageService.getCurrentLanguage();
  }

  onAvatarError(event: Event) {
    // Hide the broken image and show default avatar
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

}
